/**
 * @file React hook — useA3FormData.ts: form/state logic for contributor dashboard.
 */

import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { FORM_STAGE_CODES } from "../../../../../../constants/formStages";
import { A1_PETROL_DIESEL_EMBODIED_ENERGY_CONVERSION_FACTOR } from "../../../../../../constants/a1EmbodiedEnergyFactors";
import { FormDataResponse } from "../../../../../../interfaces/formInterfaces";
import {
  type A3BackgroundTechnologySlice,
  isA3ManufacturingSpecificityFromBackgroundField,
  readBackgroundTechnologyForA3,
} from "../../../../../../utils/a3ManufacturingSpecificityField";
import { hydrateA3RawMaterialQualityFields, collectA3MaterialQualityValuesForSave, isA3PlantMaterialQualityField } from "../../../../../../utils/a3RawMaterialQualityField";
import { getA3FormData, getSubmission as getA3Submission, saveA3Details } from "../../../../../../services/a3Service";
import { toast } from "react-toastify";
import {
  getFirstFormError,
  validateA3Form,
} from "../../shared/validateA3Form";
import { clearFieldErrorsFromMap } from "../../shared/contributorFormValidation";
import axiosClient from "../../../../../../api/axiosClient";
import { API_URLS } from "../../../../../../constants/apiUrls";
import {
  fetchCatalogRawMaterialsForMaterial,
  resolveCatalogRawMaterialIdFromBackgroundRow,
} from "../../shared/catalogRawMaterialResolution";
import {
  discoverA3EmbodiedCarbonMirroredProcessFieldName,
  isA3FuelLockedManufacturingProcessField,
  resolveA3ProcessNameForSave,
} from "../../../../../../utils/a3ManufacturingProcessResolution";
import {
  fetchManufacturingProcessesByMaterialId,
  type ManufacturingProcessRow,
} from "../../../../../../services/manufacturingProcessesPublicService";
import {
  getFuelDensities,
  type FuelDensityApiRow,
} from "../../../../../../services/a1Service";
import { fetchActiveCalorificValues } from "../../../../../../services/calorificValuesPublicService";
import type { CalorificValuePublic } from "../../../../../../services/calorificValuesPublicService";
import {
  fetchActiveCharacterizationFactors,
  type CharacterizationFactorPublic,
} from "../../../../../../services/characterizationFactorsPublicService";
import {
  applyFuelDensityUnitFieldChange,
  loadFuelFieldUnitOptions,
  normalizeUnitToken,
  type UnitSelectOption,
} from "../../shared/fuelMixReferenceAutofill";
import { resolveKgCo2eUnitId } from "../../shared/unitSelectOptions";
import { fetchUnitIdToSymbolMap } from "../../../../../../utils/fetchUnitIdToSymbolMap";
import {
  perFinishedCarbonUnitLabel,
  perFinishedCarbonUnitValue,
  perFinishedEnergyUnitLabel,
  perFinishedEnergyUnitValue,
  resolveFinishedQtyDenominatorSymbol,
} from "../../A1-A3/a1a3SummaryFromSubmission";
import {
  normalizeEmbodiedCarbonFunctionalUnitFormValue,
  resolveEmbodiedCarbonFunctionalUnitForSave,
} from "../../shared/contributorPerFunctionalUnitUnits";
import { roundEmbodiedCarbonReferenceQtyFromUnknown, roundEmbodiedCarbonFunctionalUnitFromUnknown } from "../../shared/embodiedCarbonReferenceQtyFormat";
import { buildA3ValuesFromSubmission } from "../utils/a3ApiFormPrefill";
import {
  formatA3ProcessRawMaterialsDisplay,
  mapA3ProcessApiRowToRawMaterialFormValues,
  resolveA3ProcessRawMaterialRefsFromForm,
} from "../utils/a3ProcessRawMaterials";
import {
  applyA3FuelMixToAllRowsInRecord,
  discoverA3FuelFieldNames,
  isA3FuelMixFieldKey,
  parseA3ProcessFuelDetailsRowKey,
  resolveA3FuelDensityNumericForSave,
  type A3FuelFieldNameMap,
  type A3FuelMixAutofillCtx,
  patchA3FuelRowFromFuelMix,
} from "../utils/a3FuelMixAutofill";
import {
  A3_MAX_FUELS_PER_PROCESS,
  buildA3FuelLoadLayout,
  collectA3ProcessFuelFieldNamesForShift,
  insertIndexForNewA3FuelSlot,
  processFuelFlatStart,
  processIndexForA3FuelFlatRow,
  shiftA3FuelValuesForInsert,
  shiftA3FuelValuesForRemove,
  totalA3FuelFlatRows,
} from "../utils/a3ProcessFuelLayout";
import {
  buildA3EmbodiedEnergyDetailsValueUpdates,
  buildDefaultA3EmbodiedEnergyUnitUpdates,
  computeA3CumulativeFuelEmbodiedEnergyMj,
  discoverA3EmbodiedEnergyValueAndUnitFields,
  pickPetrolDieselEmbodiedEnergyConversionFromSubmission,
  resolveA3EmbodiedEnergyMjUnitSelectValue,
} from "../utils/a3FuelEmbodiedEnergyKwh";
import {
  buildA3CarbonCharacterizationFactorUpdates,
  discoverA3CarbonCharacterizationFields,
} from "../utils/a3CarbonCharacterizationFactor";
import {
  buildA3EmbodiedCarbonFunctionalValueUpdates,
  computeA3ReferenceEmbodiedCarbonA1SimpleForProcess,
  computeA3TotalEmbodiedCarbonKgCo2e,
  discoverA3CarbonFunctionalAndRawMaterialQtyFields,
  discoverA3CarbonFunctionalUnitField,
} from "../utils/a3EmbodiedCarbonFromFuels";

/* ======================================================
   Payload Types (FIXES never[])
====================================================== */
type A3ProcessPayload = {
  rawMaterialId: number;
  rawMaterialName: string;
  processName: string;
  description: string;
  rawMaterialsInvolved: Array<{ id: number | null; name: string }>;
};

type A3PlantPayload = {
  plantCapacityId: number;
  materialQualityId?: number | null;
  materialQualityValues?: Array<{ attributeId: number; value: number }>;
  plantStandardOperationTemperature: number;
  temperatureUnitId: number;
  plantStandardOperationPressure: number;
  pressureUnitId: number;
};

type A3FuelPayload = {
  rawMaterialId: number;
  rawMaterialName: string;
  processName: string;
  fuelMix: string;
  fuelQuantity: number;
  fuelQuantityUnitId: number;
  fuelDensity: number;
  fuelDensityUnitId: number;
  calorificValue: number;
  calorificValueUnitId: number;
};

type A3EnergyPayload = {
  rawMaterialId: number;
  rawMaterialName: string;
  processName: string;
  embodiedEnergy: number;
  unitId: number;
};

type A3CarbonPayload = {
  rawMaterialId: number;
  rawMaterialName: string;
  processName: string;
  characterizationFactor: number;
  characterizationFactorUnit: number;
  referenceQty: number;
  referenceQtyUnitId: number;
  functionalUnitCarbon: number;
  functionalUnitCarbonUnitId: number;
};

const A3_ENERGY_CODE = "A3_EMBODIED_ENERGY_DETAILS";
const A3_CARBON_CODE = "A3_EMBODIED_CARBON_DETAILS";

/** A3 save sends structured `rawMaterialsInvolved`; legacy `description` holds comma-separated names. */
function getA3ProcessRawMaterialsForSave(
  formData: FormDataResponse,
  values: Record<string, string>,
  rowIndex: number,
  backgroundOptions: Array<{ id: number; name: string }>
): {
  rawMaterialsInvolved: Array<{ id: number | null; name: string }>;
  description: string;
} {
  const sec = formData.formSubSections?.find(
    (s) => s.code === "A3_PROCESS_DETAILS"
  );
  const field = sec?.fields.find((f) => f.type === "RAW_MATERIALS");
  const base =
    field?.name != null
      ? `${field.name}_A3_PROCESS_DETAILS_row${rowIndex}`
      : `processRawMaterials_A3_01_A3_PROCESS_DETAILS_row${rowIndex}`;

  const refs = resolveA3ProcessRawMaterialRefsFromForm(
    values[base] ?? "",
    values[`${base}_label`] ?? "",
    backgroundOptions
  );

  return {
    rawMaterialsInvolved: refs,
    description: formatA3ProcessRawMaterialsDisplay(refs),
  };
}

/**
 * API rows use `fuelDensity`; CMS may use `A3ManufacturingProcessFuelDensity`.
 * Also fixes a CMS key that accidentally holds the same text as `processName`.
 */
function syncA3FuelDensityFieldsFromApi(
  init: Record<string, string>,
  formData: FormDataResponse,
  rowCount: number,
  a3FuelRows: unknown[] | undefined,
  fuelDensityCatalog: FuelDensityApiRow[]
): void {
  const fuelCode = "A3_PROCESS_FUEL_DETAILS";
  const sec = formData.formSubSections?.find((s) => s.code === fuelCode);
  if (!sec?.fields?.length || !Array.isArray(a3FuelRows)) return;

  for (let ri = 0; ri < rowCount; ri++) {
    const row = a3FuelRows[ri] as Record<string, unknown> | undefined;
    const suffix = `_${fuelCode}_row${ri}`;
    const processVal = String(init[`processName${suffix}`] ?? "").trim();
    const mergedApiFuelDensity = String(
      init[`fuelDensity${suffix}`] ?? ""
    ).trim();
    const fromRow = row?.fuelDensity ?? row?.FuelDensity;
    const apiDensityStr =
      fromRow !== undefined && fromRow !== null
        ? String(fromRow).trim()
        : mergedApiFuelDensity;

    const parsedApi = Number.parseFloat(apiDensityStr);
    const looksNum =
      apiDensityStr !== "" && Number.isFinite(parsedApi);
    if (!looksNum) continue;

    for (const f of sec.fields) {
      if (isA3FuelLockedManufacturingProcessField(f, fuelCode)) continue;
      const fname = (f.name || "").toLowerCase();
      const isFuelDensityCmsField =
        (fname.includes("fueldensity") ||
          fname.includes("fuelconsumptiondensity")) &&
        !fname.includes("unit");
      if (!isFuelDensityCmsField) continue;
      const cmsKey = `${f.name}_${fuelCode}_row${ri}`;
      const cur = String(init[cmsKey] ?? "").trim();
      const curLooksNum =
        cur !== "" && Number.isFinite(Number.parseFloat(cur));
      if (
        cur === "" ||
        (processVal !== "" && cur === processVal) ||
        (cur !== "" && !curLooksNum)
      ) {
        let toWrite = apiDensityStr;
        if (fuelDensityCatalog.length) {
          const rowMatch = fuelDensityCatalog.find(
            (r) =>
              String(r.densityKgL ?? "").trim() === apiDensityStr ||
              String(r.densityKgM3 ?? "").trim() === apiDensityStr ||
              Number(r.densityKgL) === parsedApi ||
              Number(r.densityKgM3) === parsedApi
          );
          if (rowMatch) {
            const kgL = String(rowMatch.densityKgL ?? "").trim();
            const kgM = String(rowMatch.densityKgM3 ?? "").trim();
            toWrite = kgM || kgL || apiDensityStr;
          }
        }
        init[cmsKey] = toWrite;
      }
    }
  }
}

/* ======================================================
   Hook
====================================================== */
export function useA3FormData(submissionId?: string) {
  const [formData, setFormData] = useState<FormDataResponse | null>(null);
  const [values, setValues] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(false);
  /** Bumps when GET /units map is refreshed so embodied-energy unit ids re-sync. */
  const [a3UnitsIndexTick, setA3UnitsIndexTick] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});

  /** Finished product `materials.id` (Background.materialId) for GET /manufacturing-processes. */
  const [finishedMaterialId, setFinishedMaterialId] = useState<number | null>(null);

  /** Background Data — finished construction material declared quantity (denominator for per-unit EE). */
  const [backgroundFinishedDeclaredQty, setBackgroundFinishedDeclaredQty] =
    useState<string>("");

  /** Background finished material unit symbol (e.g. kg) from subMaterialUnit. */
  const [backgroundFinishedMaterialUnitSymbol, setBackgroundFinishedMaterialUnitSymbol] =
    useState<string | null>(null);

  /** Petrol/diesel MJ → kWh; default 0.278, override when present on submission/API. */
  const [petrolDieselConversionFactor, setPetrolDieselConversionFactor] =
    useState<number>(A1_PETROL_DIESEL_EMBODIED_ENERGY_CONVERSION_FACTOR);

  const [characterizationFactors, setCharacterizationFactors] = useState<
    CharacterizationFactorPublic[]
  >([]);
  /** When user edits A3 embodied-carbon characterization fields, skip auto overwrite (Form A1 pattern). */
  const a3CfManualFromUserRef = useRef<Record<string, boolean>>({});
  /**
   * When user edits A3 embodied-carbon reference (EE×CF) or functional unit outputs, skip auto overwrite
   * (same idea as A1 `embodiedCarbonDerivedManualRef`).
   */
  const a3CarbonDerivedManualRef = useRef<Record<string, boolean>>({});

  /** Read-only Manufacturing Specificity (MANUFACTURER_SPECIFICATION) from background.technology. */
  const [manufacturingSpecificityFromBackground, setManufacturingSpecificityFromBackground] =
    useState<{ id: string; display: string } | null>(null);

  /** Full background slice so A3 can resolve `technology` even if React state is stale. */
  const [a3SubmissionBackground, setA3SubmissionBackground] =
    useState<A3BackgroundTechnologySlice | null>(null);

  /** Catalog raw materials for the finished material, limited to rows declared in Background Data. */
  const [backgroundRawMaterialOptions, setBackgroundRawMaterialOptions] = useState<
    { id: number; name: string }[]
  >([]);

  /** Manufacturing Process Details (Sub Processes) — one form row per sub-process. */
  const [a3ProcessDetailsRowCount, setA3ProcessDetailsRowCount] = useState(1);

  /** Fuel rows per sub-process (flat keys `_A3_PROCESS_FUEL_DETAILS_rowN`); length === process row count. */
  const [a3FuelsPerProcess, setA3FuelsPerProcess] = useState<number[]>([1]);
  const a3FuelsPerProcessRef = useRef<number[]>([1]);
  const a3ProcessDetailsRowCountRef = useRef(1);

  useEffect(() => {
    a3FuelsPerProcessRef.current = a3FuelsPerProcess;
  }, [a3FuelsPerProcess]);

  useEffect(() => {
    a3ProcessDetailsRowCountRef.current = a3ProcessDetailsRowCount;
  }, [a3ProcessDetailsRowCount]);

  /** GET /manufacturing-processes — resolve stored process ids to names for save and fuel card headers. */
  const [a3ManufacturingProcessesCatalog, setA3ManufacturingProcessesCatalog] =
    useState<ManufacturingProcessRow[]>([]);

  /** GET /fuel-densities — fuel density dropdown in Manufacturing Process Fuel Consumption. */
  const [fuelDensityCatalogUi, setFuelDensityCatalogUi] = useState<
    FuelDensityApiRow[]
  >([]);
  const [calorificValuesCatalogUi, setCalorificValuesCatalogUi] = useState<
    CalorificValuePublic[]
  >([]);
  const [fuelDensityUnitOptsUi, setFuelDensityUnitOptsUi] = useState<
    UnitSelectOption[]
  >([]);

  const a3FuelDensitiesRef = useRef<FuelDensityApiRow[]>([]);
  const a3CalorificValuesRef = useRef<CalorificValuePublic[]>([]);
  const a3FuelUnitOptsRef = useRef<{
    density: UnitSelectOption[];
    calorific: UnitSelectOption[];
  }>({ density: [], calorific: [] });
  const a3UnitTokenMapRef = useRef<Record<string, number>>({});
  const a3UnitIdToTokenRef = useRef<Record<string, string>>({});
  const a3FuelFieldNamesRef = useRef<A3FuelFieldNameMap>({});
  const formDataRef = useRef<FormDataResponse | null>(null);

  /** Cumulative embodied energy (MJ) from all A3 fuel rows. */
  const embodiedEnergyFromFuelsMj = useMemo(
    () =>
      computeA3CumulativeFuelEmbodiedEnergyMj({
        formData,
        values,
        fuelsPerProcess: a3FuelsPerProcess,
        fuelNames: discoverA3FuelFieldNames(formData),
        unitTokenById: a3UnitIdToTokenRef.current,
      }),
    [formData, values, a3FuelsPerProcess, a3UnitsIndexTick]
  );

  /** Total MJ ÷ background finished material declared qty (when qty > 0). */
  const embodiedEnergyPerDeclaredFinishedMaterialMj = useMemo(() => {
    const q = parseFloat(String(backgroundFinishedDeclaredQty).trim());
    if (!Number.isFinite(q) || q <= 0) return null;
    if (embodiedEnergyFromFuelsMj <= 0) return null;
    return Math.round((embodiedEnergyFromFuelsMj / q) * 1000) / 1000;
  }, [embodiedEnergyFromFuelsMj, backgroundFinishedDeclaredQty]);

  /** Cumulative embodied carbon (kg CO₂eq) — sum over all A3 fuel rows. */
  const a3TotalEmbodiedCarbonKgCo2e = useMemo(
    () =>
      computeA3TotalEmbodiedCarbonKgCo2e({
        formData,
        values,
        fuelsPerProcess: a3FuelsPerProcess,
        characterizationFactors,
        unitTokenById: a3UnitIdToTokenRef.current,
      }),
    [formData, values, a3FuelsPerProcess, characterizationFactors, a3UnitsIndexTick]
  );

  /** Total embodied carbon ÷ Background finished material declared qty (kg CO₂e per finished qty). */
  const embodiedCarbonPerDeclaredFinishedMaterialKg = useMemo(() => {
    const q = parseFloat(String(backgroundFinishedDeclaredQty).trim());
    if (!Number.isFinite(q) || q <= 0) return null;
    if (a3TotalEmbodiedCarbonKgCo2e <= 0) return null;
    return Math.round((a3TotalEmbodiedCarbonKgCo2e / q) * 1000) / 1000;
  }, [a3TotalEmbodiedCarbonKgCo2e, backgroundFinishedDeclaredQty]);

  const perFinishedEnergyUnitLabelDisplay = useMemo(
    () => perFinishedEnergyUnitLabel(backgroundFinishedMaterialUnitSymbol),
    [backgroundFinishedMaterialUnitSymbol]
  );

  const perFinishedCarbonUnitLabelDisplay = useMemo(
    () => perFinishedCarbonUnitLabel(backgroundFinishedMaterialUnitSymbol),
    [backgroundFinishedMaterialUnitSymbol]
  );

  const functionalCarbonUnitValue = useMemo(
    () => perFinishedCarbonUnitValue(backgroundFinishedMaterialUnitSymbol),
    [backgroundFinishedMaterialUnitSymbol]
  );

  /** Same naming as save `summary.embodiedCarbonValue` — per declared finished quantity. */
  const carbonTotal = embodiedCarbonPerDeclaredFinishedMaterialKg ?? 0;

  /** Recompute A3 Embodied Energy Details rows when manufacturing fuel inputs change (A1-style). */
  const a3EmbodiedEnergyFromFuelFingerprint = useMemo(() => {
    if (!formData) return "";
    const names = discoverA3FuelFieldNames(formData);
    if (!names.mix) return "";
    const n = totalA3FuelFlatRows(a3FuelsPerProcess);
    const q = names.quantity ?? "fuelQuantity_A3_01";
    const qUnit = names.quantityUnit ?? "fuelQuantity_unit_A3_01";
    const density = names.density ?? "fuelDensity_A3_01";
    const cal =
      names.calorific ?? "A3ManufacturingProcessFuelCalorificValue";
    let s = "";
    for (let i = 0; i < n; i++) {
      s += `${values[`${names.mix}_A3_PROCESS_FUEL_DETAILS_row${i}`] ?? ""}\u001f${
        values[`${q}_A3_PROCESS_FUEL_DETAILS_row${i}`] ?? ""
      }\u001f${values[`${qUnit}_A3_PROCESS_FUEL_DETAILS_row${i}`] ?? ""}\u001f${
        values[`${density}_A3_PROCESS_FUEL_DETAILS_row${i}`] ?? ""
      }\u001f${values[`${cal}_A3_PROCESS_FUEL_DETAILS_row${i}`] ?? ""}|`;
    }
    s += `\u001e${a3ProcessDetailsRowCount}\u001e${a3FuelsPerProcess.join(",")}`;
    return s;
  }, [
    formData,
    values,
    a3FuelsPerProcess,
    a3ProcessDetailsRowCount,
  ]);

  const a3EmbodiedCarbonSyncFingerprint = useMemo(() => {
    if (!formData) return "";
    const dm = discoverA3CarbonFunctionalAndRawMaterialQtyFields(formData);
    let rawPart = "";
    const P = Math.max(1, a3ProcessDetailsRowCount);
    for (let p = 0; p < P; p++) {
      const rawKey = dm
        ? `${dm.rawMaterialQtyField}_${A3_CARBON_CODE}_row${p}`
        : `A3EmbodiedCarbonDetailsRawMaterialQty_${A3_CARBON_CODE}_row${p}`;
      rawPart += `${values[rawKey] ?? ""}\u001f`;
    }
    return `${a3EmbodiedEnergyFromFuelFingerprint}\u001f${rawPart}\u001f${P}\u001f${characterizationFactors.length}`;
  }, [
    a3EmbodiedEnergyFromFuelFingerprint,
    a3ProcessDetailsRowCount,
    characterizationFactors.length,
    formData,
    values,
  ]);

  const getA3EmbodiedEnergyMjUnitId = useCallback(
    () => resolveA3EmbodiedEnergyMjUnitSelectValue(a3UnitTokenMapRef.current),
    []
  );

  useEffect(() => {
    if (!formData || loading) return;
    const unitU = buildDefaultA3EmbodiedEnergyUnitUpdates(
      formData,
      values,
      a3ProcessDetailsRowCount,
      { mjUnitId: getA3EmbodiedEnergyMjUnitId() }
    );
    const updates = buildA3EmbodiedEnergyDetailsValueUpdates(
      formData,
      { ...values, ...unitU },
      a3ProcessDetailsRowCount,
      a3FuelsPerProcess,
      {
        mjUnitId: getA3EmbodiedEnergyMjUnitId(),
        unitTokenById: a3UnitIdToTokenRef.current,
      }
    );
    const merged = { ...unitU, ...updates };
    let changed = false;
    for (const k of Object.keys(merged)) {
      if (values[k] !== merged[k]) {
        changed = true;
        break;
      }
    }
    if (!changed) return;
    setValues((v) => ({ ...v, ...merged }));
    // eslint-disable-next-line react-hooks/exhaustive-deps -- fuel fingerprint drives sync; `values` read from latest render
  }, [
    a3EmbodiedEnergyFromFuelFingerprint,
    a3UnitsIndexTick,
    formData,
    loading,
    a3ProcessDetailsRowCount,
    a3FuelsPerProcess,
    getA3EmbodiedEnergyMjUnitId,
  ]);

  useEffect(() => {
    if (!formData || loading) return;
    const updates = buildA3CarbonCharacterizationFactorUpdates(
      formData,
      values,
      a3ProcessDetailsRowCount,
      a3FuelsPerProcess,
      characterizationFactors,
      a3CfManualFromUserRef.current,
      a3UnitTokenMapRef.current
    );
    let changed = false;
    for (const k of Object.keys(updates)) {
      if (values[k] !== updates[k]) {
        changed = true;
        break;
      }
    }
    if (!changed) return;
    setValues((v) => ({ ...v, ...updates }));
    // eslint-disable-next-line react-hooks/exhaustive-deps -- fuel fingerprint + CF API; `values` from latest render
  }, [
    a3EmbodiedEnergyFromFuelFingerprint,
    a3UnitsIndexTick,
    a3ProcessDetailsRowCount,
    characterizationFactors,
    formData,
    loading,
    a3FuelsPerProcess,
  ]);

  useEffect(() => {
    if (!formData || loading) return;
    const updates = buildA3EmbodiedCarbonFunctionalValueUpdates(
      formData,
      values,
      a3ProcessDetailsRowCount,
      a3FuelsPerProcess,
      characterizationFactors,
      a3CarbonDerivedManualRef.current,
      {
        unitTokenById: a3UnitIdToTokenRef.current,
        kgCo2eUnitId: resolveKgCo2eUnitId(a3UnitTokenMapRef.current),
        functionalCarbonUnitValue,
      }
    );
    let changed = false;
    for (const k of Object.keys(updates)) {
      if (values[k] !== updates[k]) {
        changed = true;
        break;
      }
    }
    if (!changed) return;
    setValues((v) => ({ ...v, ...updates }));
    // eslint-disable-next-line react-hooks/exhaustive-deps -- fuel + raw qty fingerprint; `values` from latest render
  }, [
    a3EmbodiedCarbonSyncFingerprint,
    a3ProcessDetailsRowCount,
    characterizationFactors,
    formData,
    loading,
    a3FuelsPerProcess,
    a3UnitsIndexTick,
    functionalCarbonUnitValue,
  ]);

  useLayoutEffect(() => {
    if (loading || !formData || !backgroundFinishedMaterialUnitSymbol?.trim()) {
      return;
    }
    const functionalUnitField = discoverA3CarbonFunctionalUnitField(formData);
    const kgCo2eUnitId = resolveKgCo2eUnitId(a3UnitTokenMapRef.current);
    const perUnit = perFinishedCarbonUnitValue(backgroundFinishedMaterialUnitSymbol);
    if (!perUnit) return;

    setValues((prev) => {
      const updates: Record<string, string> = {};
      for (let p = 0; p < a3ProcessDetailsRowCount; p++) {
        const unitKey = `${functionalUnitField}_${A3_CARBON_CODE}_row${p}`;
        const normalized = normalizeEmbodiedCarbonFunctionalUnitFormValue(
          prev[unitKey] ?? "",
          backgroundFinishedMaterialUnitSymbol,
          kgCo2eUnitId
        );
        if (normalized && prev[unitKey] !== normalized) {
          updates[unitKey] = normalized;
        }
      }
      if (!Object.keys(updates).length) return prev;
      return { ...prev, ...updates };
    });
  }, [
    loading,
    formData,
    backgroundFinishedMaterialUnitSymbol,
    a3ProcessDetailsRowCount,
    a3UnitsIndexTick,
  ]);

  const getA3FuelMixAutofillCtx = useCallback((): A3FuelMixAutofillCtx => {
    return {
      fuelDensities: a3FuelDensitiesRef.current,
      calorificValues: a3CalorificValuesRef.current,
      densityUnitOptions: a3FuelUnitOptsRef.current.density,
      calorificUnitOptions: a3FuelUnitOptsRef.current.calorific,
      unitIdByToken: a3UnitTokenMapRef.current,
    };
  }, []);

  const loadA3FuelReferenceCaches = useCallback(
    async (data: FormDataResponse) => {
      const names = discoverA3FuelFieldNames(data);
      a3FuelFieldNamesRef.current = names;
      const [fd, cv] = await Promise.all([
        getFuelDensities().catch(() => [] as FuelDensityApiRow[]),
        fetchActiveCalorificValues().catch(() => [] as CalorificValuePublic[]),
      ]);
      a3FuelDensitiesRef.current = fd;
      setFuelDensityCatalogUi(fd);
      a3CalorificValuesRef.current = cv;
      setCalorificValuesCatalogUi(cv);
      const map: Record<string, number> = {};
      const byId: Record<string, string> = {};
      try {
        const res = await axiosClient.get("/units");
        const raw = res?.data?.data ?? res?.data;
        const addToken = (tok: string | undefined, id: number) => {
          if (!tok) return;
          const t = String(tok).trim().toLowerCase();
          if (t) map[t] = id;
        };
        type UnitLike = {
          id?: unknown;
          symbol?: unknown;
          unitSymbol?: unknown;
          name?: unknown;
          displayName?: unknown;
        };
        const consumeUnit = (u: UnitLike) => {
          if (u?.id == null) return;
          const id = Number(u.id);
          if (!Number.isFinite(id)) return;
          const pick =
            String(u.symbol ?? u.unitSymbol ?? "").trim() ||
            String(u.name ?? u.displayName ?? "").trim();
          if (pick) byId[String(id)] = pick;
          addToken(String(u.symbol ?? ""), id);
          addToken(String(u.unitSymbol ?? ""), id);
          addToken(String(u.name ?? ""), id);
          addToken(String(u.displayName ?? ""), id);
        };
        if (Array.isArray(raw)) {
          raw.forEach((propertyOrUnit: { units?: unknown[] }) => {
            if (Array.isArray(propertyOrUnit?.units)) {
              propertyOrUnit.units!.forEach((u) =>
                consumeUnit(u as UnitLike)
              );
            } else {
              consumeUnit(propertyOrUnit as UnitLike);
            }
          });
        } else if (
          raw &&
          Array.isArray((raw as { units?: unknown[] }).units)
        ) {
          (raw as { units: unknown[] }).units.forEach((u) =>
            consumeUnit(u as UnitLike)
          );
        }
      } catch {
        // ignore
      }
      a3UnitTokenMapRef.current = map;
      a3UnitIdToTokenRef.current = byId;
      setA3UnitsIndexTick((t) => t + 1);
      const sec = data.formSubSections?.find(
        (s) => s.code === "A3_PROCESS_FUEL_DETAILS"
      );
      const [dOpts, cOpts] = await Promise.all([
        loadFuelFieldUnitOptions(sec, names.densityUnit ?? ""),
        loadFuelFieldUnitOptions(sec, names.calorificUnit ?? ""),
      ]);
      a3FuelUnitOptsRef.current = { density: dOpts, calorific: cOpts };
      setFuelDensityUnitOptsUi(dOpts);
    },
    []
  );

  // Save/dirty tracking (same UX as A1/A2)
  const lastSavedRef = useRef<Record<string, string>>({});
  const valuesRef = useRef<Record<string, string>>({});
  const snapshotInitializedRef = useRef(false);
  /** After load, capture baseline once autofill effects have committed (avoids stuck isDirty / disabled Next). */
  const pendingBaselineCaptureRef = useRef(false);
  const baselineIdleTimerRef = useRef<number | null>(null);
  const [hasSavedOnce, setHasSavedOnce] = useState<boolean>(false);
  const [isDirty, setIsDirty] = useState<boolean>(false);

  /** Compare union of keys; treat missing vs "" as equal (init vs autofill key skew). */
  const recordsEqual = (a: Record<string, string>, b: Record<string, string>) => {
    const seen: Record<string, boolean> = {};
    const ak = Object.keys(a || {});
    const bk = Object.keys(b || {});
    for (let i = 0; i < ak.length; i++) seen[ak[i]] = true;
    for (let i = 0; i < bk.length; i++) seen[bk[i]] = true;
    const keys = Object.keys(seen);
    for (let i = 0; i < keys.length; i++) {
      const k = keys[i];
      const av = (a as Record<string, string | undefined>)[k] ?? "";
      const bv = (b as Record<string, string | undefined>)[k] ?? "";
      if (av !== bv) return false;
    }
    return true;
  };

  /* ======================================================
     LOAD + PREFILL
  ====================================================== */
  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        snapshotInitializedRef.current = false;
        pendingBaselineCaptureRef.current = false;
        if (baselineIdleTimerRef.current) {
          window.clearTimeout(baselineIdleTimerRef.current);
          baselineIdleTimerRef.current = null;
        }
        lastSavedRef.current = {};
        setIsDirty(false);
        setHasSavedOnce(false);
        setFinishedMaterialId(null);
        setBackgroundRawMaterialOptions([]);
        setA3ProcessDetailsRowCount(1);
        setA3FuelsPerProcess([1]);
        a3FuelsPerProcessRef.current = [1];
        a3ProcessDetailsRowCountRef.current = 1;
        setManufacturingSpecificityFromBackground(null);
        setA3SubmissionBackground(null);
        setBackgroundFinishedDeclaredQty("");
        setPetrolDieselConversionFactor(
          A1_PETROL_DIESEL_EMBODIED_ENERGY_CONVERSION_FACTOR
        );
        setCharacterizationFactors([]);
        a3CfManualFromUserRef.current = {};
        a3CarbonDerivedManualRef.current = {};

        let s: any = null;
        if (submissionId) {
          const subResult = await getA3Submission(submissionId);
          if (subResult.success && subResult.data) {
            s = subResult.data;
          } else {
            const errorMsg = subResult.error || "Failed to load submission data";
            setError(errorMsg);
            toast.error(errorMsg);
          }
        }

        const bgMidEarly = s ? Number(s.background?.materialId) : NaN;
        const materialIdForForm =
          Number.isFinite(bgMidEarly) && bgMidEarly > 0 ? bgMidEarly : undefined;

        const formResult = await getA3FormData(
          FORM_STAGE_CODES.A3_RAW_MATERIAL_MANUFACTURING,
          { materialId: materialIdForForm }
        );

        if (!formResult.success || !formResult.data) {
          const errorMsg = formResult.error || "Failed to load form data";
          setError(errorMsg);
          toast.error(errorMsg);
          setLoading(false);
          return;
        }

        const data: FormDataResponse = formResult.data;
        setFormData(data);
        await loadA3FuelReferenceCaches(data);

        let cfList: CharacterizationFactorPublic[] = [];
        try {
          cfList = await fetchActiveCharacterizationFactors();
        } catch (cfErr) {
          console.warn("[A3] characterization factors unavailable:", cfErr);
        }
        setCharacterizationFactors(cfList);

        const processRowsForInit =
          s && Array.isArray(s.A3_Process)
            ? Math.max(1, s.A3_Process.length)
            : 1;

        const fuelLayout = s
          ? buildA3FuelLoadLayout(s.A3_Process, s.A3_Fuel)
          : null;
        const fuelFlatRowCount = fuelLayout
          ? Math.max(1, fuelLayout.flatItems.length)
          : 1;

        const init: Record<string, string> = {};

        data.formSubSections?.forEach((sec) => {
          const rowN =
            sec.code === "A3_PROCESS_DETAILS" ||
            sec.code === A3_ENERGY_CODE ||
            sec.code === A3_CARBON_CODE
              ? processRowsForInit
              : sec.code === "A3_PROCESS_FUEL_DETAILS"
                ? fuelFlatRowCount
                : 1;
          for (let ri = 0; ri < rowN; ri++) {
            sec.fields.forEach((f) => {
              init[`${f.name}_${sec.code}_row${ri}`] = "";
            });
          }
        });

        if (!s) {
          await hydrateA3RawMaterialQualityFields(init, data, undefined, {
            A3_PROCESS_DETAILS: 1,
            A3_PROCESS_FUEL_DETAILS: 1,
            [A3_ENERGY_CODE]: 1,
            [A3_CARBON_CODE]: 1,
          });
          applyA3FuelMixToAllRowsInRecord(
            init,
            data,
            1,
            getA3FuelMixAutofillCtx(),
            a3FuelFieldNamesRef.current
          );
          setBackgroundRawMaterialOptions([]);
          setA3ProcessDetailsRowCount(1);
          setA3FuelsPerProcess([1]);
          a3FuelsPerProcessRef.current = [1];
          a3ProcessDetailsRowCountRef.current = 1;
          setValues(init);
          pendingBaselineCaptureRef.current = true;
          snapshotInitializedRef.current = false;
          setIsDirty(false);
          setLoading(false);
          return;
        }

        const bgMid = Number(s.background?.materialId);
        setFinishedMaterialId(
          Number.isFinite(bgMid) && bgMid > 0 ? bgMid : null
        );

        const bgFinished =
          s.background?.finishedQuantity ??
          s.background?.finished_quantity;
        setBackgroundFinishedDeclaredQty(
          bgFinished != null && String(bgFinished).trim() !== ""
            ? String(bgFinished).trim()
            : ""
        );

        try {
          const unitMap = await fetchUnitIdToSymbolMap();
          setBackgroundFinishedMaterialUnitSymbol(
            resolveFinishedQtyDenominatorSymbol(s, unitMap)
          );
        } catch {
          setBackgroundFinishedMaterialUnitSymbol(
            resolveFinishedQtyDenominatorSymbol(s, {})
          );
        }

        setPetrolDieselConversionFactor(
          pickPetrolDieselEmbodiedEnergyConversionFromSubmission(s)
        );

        /* ---------- Material and Sub Material from backgroundRawMats ---------- */
        const backgroundRawMats = s.backgroundRawMats || [];

        let filteredBackgroundRawMaterialOptions: { id: number; name: string }[] =
          [];
        if (Number.isFinite(bgMid) && bgMid > 0) {
          let catalogList: { id: number; name: string }[] = [];
          try {
            catalogList = await fetchCatalogRawMaterialsForMaterial(bgMid);
          } catch (catErr) {
            console.warn(
              "[A3] Failed to load catalog raw materials for material:",
              catErr
            );
          }
          const allowed = new Set<number>();
          for (const row of backgroundRawMats) {
            const cid = resolveCatalogRawMaterialIdFromBackgroundRow(
              row as Record<string, unknown>,
              catalogList
            );
            if (cid != null && cid > 0) allowed.add(cid);
          }
          filteredBackgroundRawMaterialOptions = catalogList.filter((c) =>
            allowed.has(c.id)
          );
        }
        setBackgroundRawMaterialOptions(filteredBackgroundRawMaterialOptions);

        // Fetch Material and Sub Material data by ID
        let materialName = "";
        let subMaterialName = "";
        
        const firstBgRaw = backgroundRawMats[0];
        const materialIdForApi =
          firstBgRaw?.rawMaterialId ??
          s.background?.materialId ??
          null;

        // Fetch Material by ID if available (use rawMaterialId / background.materialId, not junction row id)
        if (materialIdForApi != null && materialIdForApi !== "") {
          try {
            const materialRes = await axiosClient.get(
              API_URLS.MATERIALS.BY_ID(materialIdForApi)
            );
            if (materialRes.data?.success && materialRes.data?.data) {
              materialName =
                materialRes.data.data.name ||
                materialRes.data.data.materialName ||
                "";
            }
          } catch (err) {
            console.warn("Failed to fetch material by ID:", err);
            materialName =
              firstBgRaw?.rawMaterialName ??
              firstBgRaw?.materialName ??
              "";
          }
        } else if (firstBgRaw) {
          materialName =
            firstBgRaw.rawMaterialName ??
            firstBgRaw.materialName ??
            "";
        } else if (s.background?.materialId) {
          try {
            const materialRes = await axiosClient.get(API_URLS.MATERIALS.BY_ID(s.background.materialId));
            if (materialRes.data?.success && materialRes.data?.data) {
              materialName = materialRes.data.data.name || materialRes.data.data.materialName || "";
            }
          } catch (err) {
            console.warn("Failed to fetch material by ID:", err);
            materialName = s.background.materialCode || "";
          }
        }
        
        // Fetch Sub Material by ID if available
        if (s.background?.subMaterialId) {
          try {
            console.log("🔍 Fetching Sub Material by ID:", s.background.subMaterialId);
            const subMaterialRes = await axiosClient.get(API_URLS.SUB_MATERIALS.BY_ID(s.background.subMaterialId));
            console.log("📦 Sub Material API Response:", subMaterialRes.data);
            if (subMaterialRes.data?.success && subMaterialRes.data?.data) {
              subMaterialName = subMaterialRes.data.data.name || 
                                subMaterialRes.data.data.subMaterialName || 
                                subMaterialRes.data.data.unit || 
                                subMaterialRes.data.data.displayName || "";
              console.log("✅ Sub Material name fetched:", subMaterialName);
            } else {
              console.warn("⚠️ Sub Material API response not successful:", subMaterialRes.data);
              subMaterialName = s.background.subMaterialUnit || "";
            }
          } catch (err) {
            console.error("❌ Failed to fetch sub-material by ID:", err);
            // Fallback to subMaterialUnit or empty
            subMaterialName = s.background.subMaterialUnit || "";
          }
        } else {
          console.warn("⚠️ No subMaterialId found in background:", s.background);
        }
        
        // Find Material and Sub Material fields in form sections and set them
        data.formSubSections?.forEach((sec) => {
          sec.fields.forEach((f) => {
            const fieldKey = `${f.name}_${sec.code}_row0`;
            const labelLower = f.label?.toLowerCase().trim() || "";
            
            // Material / raw material (labels vary by form schema)
            const isMaterialField =
              labelLower === "material" ||
              labelLower === "material*" ||
              labelLower === "raw material" ||
              labelLower === "raw material*";

            const isSubMaterialField =
              labelLower === "sub material" || labelLower === "sub material*";
            
            if (isMaterialField) {
              if (materialName) {
                // For dropdown fields, try to match the value with dropdown options
                if (
                  f.type === "MATERIALS" ||
                  f.type === "DROPDOWN" ||
                  f.type === "RAW_MATERIALS"
                ) {
                  // Try to find matching option by name
                  const matchingOption = f.dropdown?.options?.find(
                    (opt) => {
                      const optLabel = opt.label?.toLowerCase() || "";
                      const materialNameLower = materialName.toLowerCase();
                      return optLabel === materialNameLower || optLabel.includes(materialNameLower);
                    }
                  );
                  if (matchingOption) {
                    init[fieldKey] = matchingOption.value;
                    init[`${fieldKey}_label`] =
                      matchingOption.label ?? materialName;
                  } else {
                    init[fieldKey] = materialName;
                    init[`${fieldKey}_label`] = materialName;
                  }
                } else {
                  init[fieldKey] = materialName;
                  init[`${fieldKey}_label`] = materialName;
                }
                const materialId =
                  firstBgRaw?.rawMaterialId ??
                  s.background?.materialId ??
                  firstBgRaw?.id;
                init["material_A3"] =
                  materialId != null && materialId !== ""
                    ? String(materialId)
                    : "";
                init["material_A3_label"] = materialName;
                console.log("✅ Set Material from API:", fieldKey, "=", init[fieldKey]);
              }
            }
            
            if (isSubMaterialField) {
              // Always try to set Sub Material if subMaterialId exists, even if name fetch failed
              if (s.background?.subMaterialId) {
                // For dropdown fields, try to match the value
                if (f.type === "DROPDOWN" || f.type === "SUBMATERIALS" || f.type === "UNIT") {
                  // First try to match by name if we have it
                  if (subMaterialName) {
                    const matchingOption = f.dropdown?.options?.find(
                      (opt) => {
                        const optLabel = opt.label?.toLowerCase() || "";
                        const optValue = opt.value?.toString() || "";
                        const subMatNameLower = subMaterialName.toLowerCase();
                        const subMatId = s.background.subMaterialId?.toString() || "";
                        return optLabel === subMatNameLower || 
                               optLabel.includes(subMatNameLower) ||
                               optValue === subMatId;
                      }
                    );
                    if (matchingOption) {
                      init[fieldKey] = matchingOption.value;
                      init[`${fieldKey}_label`] =
                        subMaterialName ||
                        matchingOption.label ||
                        String(s.background.subMaterialId);
                    } else {
                      init[fieldKey] = String(s.background.subMaterialId);
                      init[`${fieldKey}_label`] =
                        subMaterialName ||
                        String(s.background.subMaterialId);
                    }
                  } else {
                    const matchingOption = f.dropdown?.options?.find(
                      (opt) => opt.value?.toString() === s.background.subMaterialId?.toString()
                    );
                    if (matchingOption) {
                      init[fieldKey] = matchingOption.value;
                      init[`${fieldKey}_label`] =
                        subMaterialName ||
                        matchingOption.label ||
                        String(s.background.subMaterialId);
                    } else {
                      init[fieldKey] = String(s.background.subMaterialId);
                      init[`${fieldKey}_label`] =
                        subMaterialName ||
                        String(s.background.subMaterialId);
                    }
                  }
                } else {
                  init[fieldKey] =
                    subMaterialName || String(s.background.subMaterialId);
                  init[`${fieldKey}_label`] =
                    subMaterialName || String(s.background.subMaterialId);
                }
                // Also set the special subMaterial_A3 for save function
                init["subMaterial_A3"] = String(s.background.subMaterialId);
                init["subMaterial_A3_label"] = subMaterialName || s.background.subMaterialUnit || "";
                console.log("✅ Set Sub Material from API:", fieldKey, "=", init[fieldKey], "name:", subMaterialName || "N/A");
              } else {
                console.warn("⚠️ No subMaterialId available for Sub Material field:", fieldKey);
              }
            }
          });
        });
        
        // Also set material_A3 and subMaterial_A3 for backward compatibility with save function
        if (backgroundRawMats.length > 0) {
          const firstRawMat = backgroundRawMats[0];
          const mid =
            firstRawMat.rawMaterialId ??
            s.background?.materialId ??
            firstRawMat.id;
          init["material_A3"] =
            mid != null && mid !== "" ? String(mid) : "";
          init["material_A3_label"] =
            materialName ||
            firstRawMat.rawMaterialName ||
            firstRawMat.materialName ||
            "";
        }
        
        if (s.background) {
          init["subMaterial_A3"] = s.background.subMaterialId ? String(s.background.subMaterialId) : "";
          init["subMaterial_A3_label"] = subMaterialName || s.background.subMaterialUnit || "";
        }

        const prefilled = buildA3ValuesFromSubmission({
          sections: data.formSubSections ?? [],
          submission: s as Record<string, unknown>,
          backgroundRawMaterialOptions: filteredBackgroundRawMaterialOptions,
          formData: data,
        });
        Object.assign(init, prefilled.values);

        if (fuelLayout) {
          setA3FuelsPerProcess(fuelLayout.counts);
          a3FuelsPerProcessRef.current = fuelLayout.counts;
        } else {
          setA3FuelsPerProcess([1]);
          a3FuelsPerProcessRef.current = [1];
        }

        const fuelRowsForDensitySync =
          fuelLayout?.flatItems ??
          Array.from({ length: fuelFlatRowCount }, () => ({}));

        syncA3FuelDensityFieldsFromApi(
          init,
          data,
          fuelFlatRowCount,
          fuelRowsForDensitySync,
          a3FuelDensitiesRef.current
        );

        // MANUFACTURING_PROCESS controls use process id; align stored processName to selected id when possible.
        const processSec = data.formSubSections?.find(
          (sub) => sub.code === "A3_PROCESS_DETAILS"
        );
        for (let ri = 0; ri < processRowsForInit; ri++) {
          const savedProcessName = String(
            init[`processName_A3_01_A3_PROCESS_DETAILS_row${ri}`] ?? ""
          ).trim();
          if (processSec && savedProcessName) {
            for (const f of processSec.fields) {
              if (f.type !== "MANUFACTURING_PROCESS") continue;
              const fieldKey = `${f.name}_${processSec.code}_row${ri}`;
              const current = String(init[fieldKey] ?? "").trim();
              if (current) continue;
              const list = f.manufacturingProcesses ?? [];
              const match = list.find((p) => p.name === savedProcessName);
              if (match) init[fieldKey] = String(match.id);
            }
          }

          const processRow = Array.isArray(s.A3_Process)
            ? (s.A3_Process[ri] as Record<string, unknown> | undefined)
            : undefined;
          if (processSec) {
            const mapped = mapA3ProcessApiRowToRawMaterialFormValues(
              processRow,
              filteredBackgroundRawMaterialOptions
            );
            for (const f of processSec.fields) {
              if (f.type !== "RAW_MATERIALS") continue;
              const fk = `${f.name}_A3_PROCESS_DETAILS_row${ri}`;
              init[fk] = mapped.ids;
              init[`${fk}_label`] = mapped.labels;
            }
          }
        }

        setA3ProcessDetailsRowCount(processRowsForInit);
        a3ProcessDetailsRowCountRef.current = processRowsForInit;

        setA3SubmissionBackground(s.background ?? null);

        const techFromBg = readBackgroundTechnologyForA3(s.background);
        if (techFromBg.id || techFromBg.display) {
          setManufacturingSpecificityFromBackground({
            id: techFromBg.id,
            display: techFromBg.display || techFromBg.id,
          });
          data.formSubSections?.forEach((sec) => {
            const sc = sec.code ?? "";
            sec.fields.forEach((f) => {
              if (!isA3ManufacturingSpecificityFromBackgroundField(f, sc, sec.name)) {
                return;
              }
              const fieldKey = `${f.name}_${sc}_row0`;
              init[fieldKey] = techFromBg.id;
              init[`${fieldKey}_label`] = techFromBg.display || techFromBg.id;
            });
          });
        } else {
          setManufacturingSpecificityFromBackground(null);
        }

        await hydrateA3RawMaterialQualityFields(init, data, materialIdForForm, {
          A3_PROCESS_DETAILS: processRowsForInit,
          A3_PROCESS_FUEL_DETAILS: fuelFlatRowCount,
          [A3_ENERGY_CODE]: processRowsForInit,
          [A3_CARBON_CODE]: processRowsForInit,
        });

        applyA3FuelMixToAllRowsInRecord(
          init,
          data,
          fuelFlatRowCount,
          getA3FuelMixAutofillCtx(),
          a3FuelFieldNamesRef.current
        );

        setValues(init);
        pendingBaselineCaptureRef.current = true;
        snapshotInitializedRef.current = false;
        setIsDirty(false);

        const hasExistingA3 =
          (Array.isArray(s.A3_Process) && s.A3_Process.length > 0) ||
          (Array.isArray(s.A3_Plant) && s.A3_Plant.length > 0) ||
          (Array.isArray(s.A3_Fuel) && s.A3_Fuel.length > 0) ||
          (Array.isArray(s.A3_Energy) && s.A3_Energy.length > 0) ||
          (Array.isArray(s.A3_Carbon) && s.A3_Carbon.length > 0);
        setHasSavedOnce(Boolean(hasExistingA3));
      } catch (e: any) {
        const errorMsg = e?.response?.data?.message || e?.message || "An unexpected error occurred";
        setError(errorMsg);
        toast.error(errorMsg);
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [submissionId]);

  // Track dirty state
  useEffect(() => {
    if (!snapshotInitializedRef.current) return;
    const saved = lastSavedRef.current || {};
    setIsDirty(!recordsEqual(values, saved));
  }, [values]);

  // One-shot baseline after load: debounce until autofill/sync effects finish updating `values`
  // (embodied energy / carbon sync). A single setTimeout(0) keyed only on [loading, formData] can run
  // before React applies those queued updates, leaving lastSaved stale and Next stuck disabled via isDirty.
  useEffect(() => {
    if (loading || !formData || !pendingBaselineCaptureRef.current) return;

    if (baselineIdleTimerRef.current) {
      window.clearTimeout(baselineIdleTimerRef.current);
    }
    baselineIdleTimerRef.current = window.setTimeout(() => {
      baselineIdleTimerRef.current = null;
      if (!pendingBaselineCaptureRef.current) return;
      pendingBaselineCaptureRef.current = false;
      lastSavedRef.current = { ...valuesRef.current };
      snapshotInitializedRef.current = true;
      setIsDirty(false);
    }, 120);

    return () => {
      if (baselineIdleTimerRef.current) {
        window.clearTimeout(baselineIdleTimerRef.current);
        baselineIdleTimerRef.current = null;
      }
    };
  }, [values, loading, formData]);

  useEffect(() => {
    valuesRef.current = values;
  }, [values]);

  useEffect(() => {
    formDataRef.current = formData;
  }, [formData]);

  useEffect(() => {
    const mid = finishedMaterialId;
    if (mid == null || !Number.isFinite(mid) || mid <= 0) {
      setA3ManufacturingProcessesCatalog([]);
      return;
    }
    let cancelled = false;
    fetchManufacturingProcessesByMaterialId(mid)
      .then((list) => {
        if (!cancelled) setA3ManufacturingProcessesCatalog(list);
      })
      .catch(() => {
        if (!cancelled) setA3ManufacturingProcessesCatalog([]);
      });
    return () => {
      cancelled = true;
    };
  }, [finishedMaterialId]);

  /* ======================================================
     CHANGE HANDLER
  ====================================================== */
  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
      const name = e.target.name;
      setErrors((prev) => clearFieldErrorsFromMap(prev, name));
      const value = e.target.value;
      const nameLower = name.toLowerCase();
      const carbonRowM = name.match(/_A3_EMBODIED_CARBON_DETAILS_row(\d+)/);
      const rowTag = carbonRowM ? `row${carbonRowM[1]}` : "row0";
      if (
        name.includes(`_${A3_CARBON_CODE}_`) &&
        nameLower.includes("characterization")
      ) {
        a3CfManualFromUserRef.current[rowTag] = true;
      }
      if (name.includes(`_${A3_CARBON_CODE}_`)) {
        const isFunctionalCarbon =
          nameLower.includes("functional") && !nameLower.includes("unit");
        const isReferenceEmbodied =
          nameLower.includes("reference") &&
          !nameLower.includes("unit") &&
          !nameLower.includes("rawmaterial");
        if (isFunctionalCarbon || isReferenceEmbodied) {
          a3CarbonDerivedManualRef.current[rowTag] = true;
        }
      }
      setValues((prev) => {
        const fd = formDataRef.current;
        const fuelRowMatch = name.match(/_A3_PROCESS_FUEL_DETAILS_row(\d+)$/);
        if (fd && fuelRowMatch) {
          const names = discoverA3FuelFieldNames(fd);
          const rowSuffix = `_A3_PROCESS_FUEL_DETAILS_row${fuelRowMatch[1]}`;
          if (
            names.densityUnit &&
            names.density &&
            name === `${names.densityUnit}${rowSuffix}`
          ) {
            return applyFuelDensityUnitFieldChange(
              prev,
              name,
              value,
              `${names.density}${rowSuffix}`,
              a3FuelUnitOptsRef.current.density
            );
          }
        }

        let next: Record<string, string> = { ...prev, [name]: value };
        if (!fd || !isA3FuelMixFieldKey(fd, name)) {
          return next;
        }
        const parsed = parseA3ProcessFuelDetailsRowKey(name);
        if (!parsed) return next;
        /** Same pattern as A1: merge CMS-discovered density/calorific keys with the mix field that actually fired (e.g. `fuelMix_A3_01`). */
        const discovered = discoverA3FuelFieldNames(fd);
        const namesForPatch: A3FuelFieldNameMap = {
          ...discovered,
          mix: parsed.baseFieldName ?? discovered.mix,
        };
        if (!namesForPatch.mix) return next;
        const ctx = getA3FuelMixAutofillCtx();
        const patch: Record<string, string> = {};
        patchA3FuelRowFromFuelMix(
          fd,
          namesForPatch,
          parsed.rowIndex,
          value,
          ctx,
          patch
        );
        next = { ...next, ...patch };
        return next;
      });
    },
    [getA3FuelMixAutofillCtx]
  );

  const patchValues = useCallback((patch: Record<string, string>) => {
    setErrors((prev) => {
      let next = prev;
      Object.keys(patch).forEach((key) => {
        next = clearFieldErrorsFromMap(next, key);
      });
      return next;
    });
    const carbonRowRe = new RegExp(`_${A3_CARBON_CODE}_row(\\d+)`);
    for (const name of Object.keys(patch)) {
      if (!name.includes(`_${A3_CARBON_CODE}_`)) continue;
      const nameLower = name.toLowerCase();
      const m = name.match(carbonRowRe);
      const rowTag = m ? `row${m[1]}` : "row0";
      if (nameLower.includes("characterization")) {
        a3CfManualFromUserRef.current[rowTag] = true;
      }
      const isFunctionalCarbon =
        nameLower.includes("functional") && !nameLower.includes("unit");
      const isReferenceEmbodied =
        nameLower.includes("reference") &&
        !nameLower.includes("unit") &&
        !nameLower.includes("rawmaterial");
      if (isFunctionalCarbon || isReferenceEmbodied) {
        a3CarbonDerivedManualRef.current[rowTag] = true;
      }
    }
    setValues((prev) => ({ ...prev, ...patch }));
  }, []);

  const addProcessDetailsRow = useCallback(() => {
    if (!formData) return;
    const sec = formData.formSubSections?.find(
      (s) => s.code === "A3_PROCESS_DETAILS"
    );
    const fuelSec = formData.formSubSections?.find(
      (s) => s.code === "A3_PROCESS_FUEL_DETAILS"
    );
    const energySec = formData.formSubSections?.find(
      (s) => s.code === A3_ENERGY_CODE
    );
    const carbonSec = formData.formSubSections?.find(
      (s) => s.code === A3_CARBON_CODE
    );
    if (!sec) return;

    const newIdx = a3ProcessDetailsRowCountRef.current;
    const fp = [...a3FuelsPerProcessRef.current];
    const insertFlat = totalA3FuelFlatRows(fp);
    const nextFp = [...fp, 1];
    a3FuelsPerProcessRef.current = nextFp;
    setA3FuelsPerProcess(nextFp);

    const base = { ...valuesRef.current };
    sec.fields.forEach((f) => {
      base[`${f.name}_A3_PROCESS_DETAILS_row${newIdx}`] = "";
    });
    fuelSec?.fields.forEach((f) => {
      base[`${f.name}_A3_PROCESS_FUEL_DETAILS_row${insertFlat}`] = "";
    });
    energySec?.fields.forEach((f) => {
      base[`${f.name}_${A3_ENERGY_CODE}_row${newIdx}`] = "";
    });
    carbonSec?.fields.forEach((f) => {
      base[`${f.name}_${A3_CARBON_CODE}_row${newIdx}`] = "";
    });

    a3ProcessDetailsRowCountRef.current = newIdx + 1;
    setA3ProcessDetailsRowCount(newIdx + 1);

    void hydrateA3RawMaterialQualityFields(
      base,
      formData,
      finishedMaterialId ?? undefined,
      {
        A3_PROCESS_DETAILS: newIdx + 1,
        A3_PROCESS_FUEL_DETAILS: insertFlat + 1,
        [A3_ENERGY_CODE]: newIdx + 1,
        [A3_CARBON_CODE]: newIdx + 1,
      }
    ).then(() => {
      setValues(base);
    });
  }, [formData, finishedMaterialId]);

  const removeLastProcessDetailsRow = useCallback(() => {
    const rowCount = a3ProcessDetailsRowCountRef.current;
    if (rowCount <= 1) return;
    const fp = a3FuelsPerProcessRef.current;
    if (fp.length <= 1) return;

    const lastFuelCount = fp[fp.length - 1];
    const flatStart = totalA3FuelFlatRows(fp.slice(0, -1));
    const flatEnd = flatStart + lastFuelCount;
    const lastIdx = rowCount - 1;

    const nextFp = fp.slice(0, -1);
    a3FuelsPerProcessRef.current = nextFp;
    setA3FuelsPerProcess(nextFp);
    a3ProcessDetailsRowCountRef.current = rowCount - 1;
    setA3ProcessDetailsRowCount(rowCount - 1);

    setValues((prev) => {
      const next = { ...prev };
      Object.keys(next).forEach((k) => {
        const m = k.match(/_A3_PROCESS_DETAILS_row(\d+)/);
        if (m && Number(m[1]) === lastIdx) {
          delete next[k];
        }
        const mf = k.match(/_A3_PROCESS_FUEL_DETAILS_row(\d+)/);
        if (mf) {
          const ri = Number(mf[1]);
          if (ri >= flatStart && ri < flatEnd) {
            delete next[k];
          }
        }
        const me = k.match(/_A3_EMBODIED_ENERGY_DETAILS_row(\d+)/);
        if (me && Number(me[1]) === lastIdx) {
          delete next[k];
        }
        const mc = k.match(/_A3_EMBODIED_CARBON_DETAILS_row(\d+)/);
        if (mc && Number(mc[1]) === lastIdx) {
          delete next[k];
        }
      });
      return next;
    });
  }, []);

  const addFuelForProcess = useCallback(
    (processIndex: number) => {
      if (!formData) return;
      const fieldNames = collectA3ProcessFuelFieldNamesForShift(formData);
      if (fieldNames.length === 0) return;

      const fp = [...a3FuelsPerProcessRef.current];
      if (
        processIndex < 0 ||
        processIndex >= fp.length ||
        (fp[processIndex] ?? 0) >= A3_MAX_FUELS_PER_PROCESS
      ) {
        return;
      }

      const insertAt = insertIndexForNewA3FuelSlot(processIndex, fp);
      const oldTotal = totalA3FuelFlatRows(fp);
      const newTotal = oldTotal + 1;
      const nextFp = fp.map((c, i) =>
        i === processIndex ? c + 1 : c
      );
      a3FuelsPerProcessRef.current = nextFp;
      setA3FuelsPerProcess(nextFp);

      setValues((prev) =>
        shiftA3FuelValuesForInsert(
          prev,
          insertAt,
          newTotal,
          fieldNames,
          "A3_PROCESS_FUEL_DETAILS"
        )
      );
    },
    [formData]
  );

  const removeFuelForProcess = useCallback(
    (processIndex: number, slotIndex: number) => {
      if (!formData) return;
      const fieldNames = collectA3ProcessFuelFieldNamesForShift(formData);
      if (fieldNames.length === 0) return;

      const fp = [...a3FuelsPerProcessRef.current];
      if (
        processIndex < 0 ||
        processIndex >= fp.length ||
        (fp[processIndex] ?? 0) <= 1
      ) {
        return;
      }

      const flatStart = processFuelFlatStart(processIndex, fp);
      const removeAt = flatStart + slotIndex;
      const oldTotal = totalA3FuelFlatRows(fp);
      if (removeAt < 0 || removeAt >= oldTotal) return;

      const nextFp = fp.map((c, i) =>
        i === processIndex ? Math.max(1, c - 1) : c
      );
      a3FuelsPerProcessRef.current = nextFp;
      setA3FuelsPerProcess(nextFp);

      setValues((prev) =>
        shiftA3FuelValuesForRemove(
          prev,
          removeAt,
          oldTotal,
          fieldNames,
          "A3_PROCESS_FUEL_DETAILS"
        )
      );
    },
    [formData]
  );

  const v = (k: string) => values[k] ?? "";

  /* ======================================================
     SAVE (STRICTLY TYPED)
  ====================================================== */
  const save = async (): Promise<{
    success: boolean;
    message?: string;
    error?: string;
  }> => {
    if (!formData || !submissionId) {
      return { success: false, error: "Missing form or submission" };
    }

    const formErrors = validateA3Form({
      formData,
      values,
      processDetailsRowCount: a3ProcessDetailsRowCount,
      fuelsPerProcess: a3FuelsPerProcess,
      fuelDensityCatalog: fuelDensityCatalogUi,
      calorificValuesCatalog: calorificValuesCatalogUi,
    });
    if (Object.keys(formErrors).length > 0) {
      setErrors(formErrors);
      toast.error(
        getFirstFormError(formErrors) ?? "Please fix the highlighted fields."
      );
      return { success: false, error: getFirstFormError(formErrors) ?? undefined };
    }
    setErrors({});

    const payload: {
      process: A3ProcessPayload[];
      plant: A3PlantPayload[];
      steps: any[];
      fuel: A3FuelPayload[];
      energy: A3EnergyPayload[];
      carbon: A3CarbonPayload[];
      summary: {
        embodiedEnergyValue: number;
        embodiedEnergyUnit: string;
        embodiedCarbonValue: number;
        embodiedCarbonUnit: string;
      };
    } = {
      process: [],
      plant: [],
      steps: [],
      fuel: [],
      energy: [],
      carbon: [],
      summary: {
        embodiedEnergyValue: 0,
        embodiedEnergyUnit: perFinishedEnergyUnitValue(
          backgroundFinishedMaterialUnitSymbol
        ),
        embodiedCarbonValue: carbonTotal,
        embodiedCarbonUnit: perFinishedCarbonUnitValue(
          backgroundFinishedMaterialUnitSymbol
        ),
      },
    };

    const declaredForSummary = parseFloat(
      String(backgroundFinishedDeclaredQty).trim()
    );
    payload.summary.embodiedEnergyValue =
      Number.isFinite(declaredForSummary) && declaredForSummary > 0
        ? Math.round(
            (embodiedEnergyFromFuelsMj / declaredForSummary) * 1000
          ) / 1000
        : 0;

    /* ===================== PROCESS (one entry per sub-process row) ===================== */
    for (let i = 0; i < a3ProcessDetailsRowCount; i++) {
      const rawMaterials = getA3ProcessRawMaterialsForSave(
        formData,
        values,
        i,
        backgroundRawMaterialOptions
      );
      payload.process.push({
        rawMaterialId: Number(v("material_A3")),
        rawMaterialName: v("material_A3_label"),
        processName: resolveA3ProcessNameForSave(
          formData,
          values,
          i,
          a3ManufacturingProcessesCatalog
        ),
        description: rawMaterials.description,
        rawMaterialsInvolved: rawMaterials.rawMaterialsInvolved,
      });
    }

    payload.steps = payload.process.map((row) => ({
      processName: row.processName,
      rawMaterialsInvolved: row.description,
    }));

    /* ===================== PLANT ===================== */
    const plantSection = formData.formSubSections?.find(
      (s) => s.code === "A3_MANUFACTURING_PLANT_DETAILS"
    );
    const mqField = plantSection?.fields.find((f) =>
      isA3PlantMaterialQualityField(f, "A3_MANUFACTURING_PLANT_DETAILS")
    );
    const mqFieldName = mqField?.name ?? "materialQuality_A3";
    const materialQualityValues = collectA3MaterialQualityValuesForSave(
      values,
      mqFieldName,
      "A3_MANUFACTURING_PLANT_DETAILS",
      0
    );

    payload.plant.push({
      plantCapacityId: Number(
        v("plantCapacity_A3_A3_MANUFACTURING_PLANT_DETAILS_row0")
      ),
      materialQualityValues,
      plantStandardOperationTemperature: Number(
        v("plantTemperature_A3_A3_MANUFACTURING_PLANT_DETAILS_row0")
      ),
      temperatureUnitId: Number(
        v("plantTemperatureUnit_A3_A3_MANUFACTURING_PLANT_DETAILS_row0")
      ),
      plantStandardOperationPressure: Number(
        v("plantPressure_A3_A3_MANUFACTURING_PLANT_DETAILS_row0")
      ),
      pressureUnitId: Number(
        v("plantPressureUnit_A3_A3_MANUFACTURING_PLANT_DETAILS_row0")
      ),
    });

    /* ===================== FUEL (flat rows; multiple fuels per sub-process) ===================== */
    const fuelNames = discoverA3FuelFieldNames(formData);
    const densityFieldName =
      fuelNames.density ?? "A3ManufacturingProcessFuelDensity";
    const densityUnitFieldName =
      fuelNames.densityUnit ?? "A3ManufacturingProcessFuelDensityUnit";
    const mixField = fuelNames.mix ?? "fuelMix_A3_01";
    const quantityField = fuelNames.quantity ?? "fuelQuantity_A3_01";
    const quantityUnitField =
      fuelNames.quantityUnit ?? "fuelQuantityUnit_A3_01";
    const calorificField =
      fuelNames.calorific ?? "A3ManufacturingProcessFuelCalorificValue";
    const calorificUnitField =
      fuelNames.calorificUnit ??
      "A3ManufacturingProcessFuelConsumptionCalorificValueUnit";

    const fuelFlatTotal = totalA3FuelFlatRows(a3FuelsPerProcess);
    for (let flat = 0; flat < fuelFlatTotal; flat++) {
      const processIdx = processIndexForA3FuelFlatRow(
        flat,
        a3FuelsPerProcess
      );
      payload.fuel.push({
        rawMaterialId: Number(v("material_A3")),
        rawMaterialName: v("material_A3_label"),
        processName: resolveA3ProcessNameForSave(
          formData,
          values,
          processIdx,
          a3ManufacturingProcessesCatalog
        ),
        fuelMix: v(`${mixField}_A3_PROCESS_FUEL_DETAILS_row${flat}`),
        fuelQuantity: Number(
          v(`${quantityField}_A3_PROCESS_FUEL_DETAILS_row${flat}`)
        ),
        fuelQuantityUnitId: Number(
          v(`${quantityUnitField}_A3_PROCESS_FUEL_DETAILS_row${flat}`)
        ),
        fuelDensity: resolveA3FuelDensityNumericForSave(
          v(`${densityFieldName}_A3_PROCESS_FUEL_DETAILS_row${flat}`),
          a3FuelDensitiesRef.current
        ),
        fuelDensityUnitId: Number(
          v(`${densityUnitFieldName}_A3_PROCESS_FUEL_DETAILS_row${flat}`)
        ),
        calorificValue: Number(
          v(`${calorificField}_A3_PROCESS_FUEL_DETAILS_row${flat}`)
        ),
        calorificValueUnitId: Number(
          v(`${calorificUnitField}_A3_PROCESS_FUEL_DETAILS_row${flat}`)
        ),
      });
    }

    /* ===================== ENERGY (one entry per sub-process row) ===================== */
    const energyFieldNames = discoverA3EmbodiedEnergyValueAndUnitFields(formData);
    const energyValueField =
      energyFieldNames?.valueField ?? "embodiedEnergy_A3_01";
    const energyUnitField =
      energyFieldNames?.unitField ?? "embodiedEnergyUnit_A3_01";

    for (let i = 0; i < a3ProcessDetailsRowCount; i++) {
      const valueKey = `${energyValueField}_${A3_ENERGY_CODE}_row${i}`;
      const unitKey = `${energyUnitField}_${A3_ENERGY_CODE}_row${i}`;
      payload.energy.push({
        rawMaterialId: Number(v("material_A3")),
        rawMaterialName: v("material_A3_label"),
        processName: resolveA3ProcessNameForSave(
          formData,
          values,
          i,
          a3ManufacturingProcessesCatalog
        ),
        embodiedEnergy: Number(v(valueKey)),
        unitId: Number(v(unitKey)),
      });
    }

    /* ===================== CARBON (one entry per sub-process row) ===================== */
    const carbonCfNames = discoverA3CarbonCharacterizationFields(formData);
    const cfValueField =
      carbonCfNames?.valueField ?? "characterizationFactor_A3_01";
    const cfUnitField =
      carbonCfNames?.unitField ?? "characterizationFactorUnit_A3_01";

    const carbonQtyNames =
      discoverA3CarbonFunctionalAndRawMaterialQtyFields(formData);
    const functionalField =
      carbonQtyNames?.functionalField ?? "A3EmbodiedCarbonDetailsFunctional";
    const functionalUnitField = discoverA3CarbonFunctionalUnitField(formData);
    const carbonMirroredProcessField =
      discoverA3EmbodiedCarbonMirroredProcessFieldName(formData);

    for (let i = 0; i < a3ProcessDetailsRowCount; i++) {
      const referenceEmbodiedComputed =
        computeA3ReferenceEmbodiedCarbonA1SimpleForProcess(
          {
            formData,
            values,
            fuelsPerProcess: a3FuelsPerProcess,
            characterizationFactors,
            unitTokenById: a3UnitIdToTokenRef.current,
          },
          i,
          a3ProcessDetailsRowCount
        );

      const refEmbodiedKey = carbonQtyNames?.referenceEmbodiedField
        ? `${carbonQtyNames.referenceEmbodiedField}_${A3_CARBON_CODE}_row${i}`
        : null;
      const fromFormRef = refEmbodiedKey ? Number(v(refEmbodiedKey)) : NaN;
      const referenceQtyForSave = roundEmbodiedCarbonReferenceQtyFromUnknown(
        Number.isFinite(fromFormRef) && !Number.isNaN(fromFormRef)
          ? fromFormRef
          : referenceEmbodiedComputed
      );

      const refEmbodiedUnitKey = carbonQtyNames?.referenceEmbodiedUnitField
        ? `${carbonQtyNames.referenceEmbodiedUnitField}_${A3_CARBON_CODE}_row${i}`
        : null;
      const referenceQtyUnitForSave = refEmbodiedUnitKey
        ? Number(v(refEmbodiedUnitKey))
        : 0;

      const carbonProcessFromForm = carbonMirroredProcessField
        ? String(
            v(`${carbonMirroredProcessField}_${A3_CARBON_CODE}_row${i}`)
          ).trim()
        : "";
      const carbonProcessResolved = resolveA3ProcessNameForSave(
        formData,
        values,
        i,
        a3ManufacturingProcessesCatalog
      ).trim();

      payload.carbon.push({
        rawMaterialId: Number(v("material_A3")),
        rawMaterialName: v("material_A3_label"),
        processName: carbonProcessFromForm || carbonProcessResolved,
        characterizationFactor: Number(
          v(`${cfValueField}_${A3_CARBON_CODE}_row${i}`)
        ),
        characterizationFactorUnit: Number(
          v(`${cfUnitField}_${A3_CARBON_CODE}_row${i}`)
        ),
        referenceQty: referenceQtyForSave,
        referenceQtyUnitId: Number.isFinite(referenceQtyUnitForSave)
          ? referenceQtyUnitForSave
          : 0,
        functionalUnitCarbon: roundEmbodiedCarbonFunctionalUnitFromUnknown(
          v(`${functionalField}_${A3_CARBON_CODE}_row${i}`)
        ),
        functionalUnitCarbonUnitId:
          resolveEmbodiedCarbonFunctionalUnitForSave(
            v(`${functionalUnitField}_${A3_CARBON_CODE}_row${i}`),
            a3UnitTokenMapRef.current,
            backgroundFinishedMaterialUnitSymbol,
            resolveKgCo2eUnitId(a3UnitTokenMapRef.current)
          ) ?? 0,
      });
    }

    try {
      const result = await saveA3Details(submissionId, payload);
      
      if (result.success) {
        toast.success(result.message || "A3 data saved successfully!", {
          style: {
            backgroundColor: "#10b981",
            color: "#ffffff",
            fontSize: "16px",
            fontWeight: "500",
          },
        });
        lastSavedRef.current = { ...values };
        snapshotInitializedRef.current = true;
        setHasSavedOnce(true);
        setIsDirty(false);
        window.setTimeout(() => {
          lastSavedRef.current = { ...valuesRef.current };
          setIsDirty(false);
        }, 0);
        return result;
      } else {
        const errorMsg = result.error || "Failed to save A3 data";
        setError(errorMsg);
        toast.error(errorMsg);
        return result;
      }
    } catch (err: any) {
      console.error("Save error:", err);
      const errorMessage =
        err?.response?.data?.message ||
        err?.message ||
        "An unexpected error occurred while saving";
      setError(errorMessage);
      toast.error(errorMessage);
      throw err;
    }
  };

  return {
    formData,
    loading,
    error,
    errors,
    values,
    hasSavedOnce,
    isDirty,
    handleChange,
    patchValues,
    save,
    embodiedEnergyFromFuelsMj,
    /** Alias for cumulative fuel-derived MJ (FormA3 `energyTotal`). */
    energyTotal: embodiedEnergyFromFuelsMj,
    embodiedEnergyPerDeclaredFinishedMaterialMj,
    backgroundFinishedDeclaredQty,
    perFinishedEnergyUnitLabel: perFinishedEnergyUnitLabelDisplay,
    perFinishedCarbonUnitLabel: perFinishedCarbonUnitLabelDisplay,
    backgroundFinishedMaterialUnitSymbol,
    a3TotalEmbodiedCarbonKgCo2e,
    embodiedCarbonPerDeclaredFinishedMaterialKg,
    carbonTotal,
    finishedMaterialId,
    manufacturingSpecificityFromBackground,
    a3SubmissionBackground,
    backgroundRawMaterialOptions,
    a3ProcessDetailsRowCount,
    a3FuelsPerProcess,
    addProcessDetailsRow,
    removeLastProcessDetailsRow,
    addFuelForProcess,
    removeFuelForProcess,
    a3ManufacturingProcessesCatalog,
    fuelDensityCatalog: fuelDensityCatalogUi,
    fuelDensityUnitOptions: fuelDensityUnitOptsUi,
    calorificValuesCatalog: calorificValuesCatalogUi,
  };
}